import { SyntheticEvent, useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useUserDetails } from 'api/useUserDetails';
import { EEventsType } from 'constants/enums';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { getPortalIdForStore, getPublisherIdForPortal } from 'utils/navigationUtil';

import { useBigQueryEventsTracking } from './useBigQueryTracking';

export interface TrackableTab {
  id?: string;
  value?: string;
  label?: string;
}

interface UseTabTrackingOptions {
  sidebarTab: string;
  tabs: TrackableTab[];
  customNavigate?: (tabId: string) => void;
}

export const useTabTracking = ({
  sidebarTab,
  tabs,
  customNavigate
}: UseTabTrackingOptions) => {
  const navigate = useNavigate();
  const { publisherId, portalId } = useParams();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();

  const trackTab = useCallback(
    (tabId: string) => {
      const selectedTab = tabs.find(
        (tab) => tab.id === tabId || tab.value === tabId
      );
      
      if (selectedTab) {
        const sectionName = selectedTab.label || selectedTab.value;
        
        const effectivePublisherId = getPublisherIdForPortal(portalId, userDetails?.portals) || publisherId;
        const effectivePortalId = portalId
          || getPortalIdForStore(publisherId, userDetails?.portals);
        
        trackBigQueryEvent({
          eventName: EEventsType.DASHBOARD_SIDEBAR_CLICKED,
          tab: sidebarTab,
          section: String(sectionName),
          role: userDetails?.role,
          userId: userDetails?.userId,
          companyId: userDetails?.company,
          publisherId: effectivePublisherId,
          portalId: effectivePortalId
        });

        trackMixpanelEvent(EEventsType.DASHBOARD_SIDEBAR_CLICKED, {
          tab: sidebarTab,
          section: String(sectionName),
          role: userDetails?.role,
          user_id: userDetails?.userId,
          company_id: userDetails?.company,
          publisher_id: effectivePublisherId,
          portal_id: effectivePortalId
        });
      }
    },
    [tabs, sidebarTab, trackBigQueryEvent, userDetails, publisherId, portalId]
  );


  const handleTabChange = useCallback(
    (tabId: string) => {
      trackTab(tabId);

      if (customNavigate) {
        customNavigate(tabId);
      } else {
        navigate(tabId);
      }
    },
    [trackTab, navigate, customNavigate]
  );

 
  const handleTabChangeWithEvent = useCallback(
    (_event: SyntheticEvent, newValue: string) => handleTabChange(newValue),
    [handleTabChange]
  );

  return { 
    handleTabChange, 
    handleTabChangeWithEvent,
    trackTab 
  };
};
